import { ColorEnum } from '@/constants/color'
import { isWeb } from '@/constants/environment'
import { FC, RefObject, useState } from 'react'
import {
    KeyboardTypeOptions,
    ReturnKeyTypeOptions,
    StyleProp,
    StyleSheet,
    TextInput,
    TextStyle,
    View,
    ViewStyle,
} from 'react-native'

interface CustomerLookupInputProps {
    value: string
    onChange: (value: string) => void
    placeholder: string
    inputRef?: RefObject<TextInput | null>
    disabled?: boolean
    keyboardType?: KeyboardTypeOptions
    returnKeyType?: ReturnKeyTypeOptions
    onSubmitEditing?: () => void
    style?: StyleProp<ViewStyle>
    inputStyle?: StyleProp<TextStyle>
    onFocusChange?: (focused: boolean) => void
}

const webFocusResetStyle = isWeb
    ? ({
          outlineColor: 'transparent',
          outlineStyle: 'none',
          outlineWidth: 0,
      } as never)
    : undefined

const CustomerLookupInput: FC<CustomerLookupInputProps> = ({
    value,
    onChange,
    placeholder,
    inputRef,
    disabled = false,
    keyboardType,
    returnKeyType,
    onSubmitEditing,
    style,
    inputStyle,
    onFocusChange,
}) => {
    const [focused, setFocused] = useState(false)

    const handleFocus = () => {
        setFocused(true)
        onFocusChange?.(true)
    }

    const handleBlur = () => {
        setFocused(false)
        onFocusChange?.(false)
    }

    return (
        <View
            style={[
                styles.container,
                focused && styles.containerFocused,
                disabled && styles.containerDisabled,
                style,
            ]}
        >
            <TextInput
                editable={!disabled}
                keyboardType={keyboardType}
                onBlur={handleBlur}
                onChangeText={onChange}
                onFocus={handleFocus}
                onSubmitEditing={onSubmitEditing}
                placeholder={placeholder}
                placeholderTextColor={disabled ? ColorEnum.GRAY_400 : ColorEnum.GRAY_300}
                ref={inputRef}
                returnKeyType={returnKeyType}
                style={[
                    styles.input,
                    webFocusResetStyle,
                    disabled && styles.inputDisabled,
                    inputStyle,
                ]}
                value={value}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        backgroundColor: ColorEnum.WHITE,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 8,
    },
    containerFocused: {
        borderColor: ColorEnum.BRICK_700,
    },
    containerDisabled: {
        backgroundColor: ColorEnum.GRAY_200,
    },
    input: {
        color: ColorEnum.GRAY_900,
        flex: 1,
        paddingHorizontal: 16,
        paddingVertical: 10,
    },
    inputDisabled: {
        color: ColorEnum.GRAY_400,
    },
})

export default CustomerLookupInput
